import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon, SparklesIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { detailQuestions, statusLabels } from '../data/demandes';
import { useRequests } from '../contexts/RequestsContext';
import { StatusProgress } from '../components/demandes/RequestList';
import { RequestDetailsPanel } from '../components/demandes/RequestDetailsPanel';
import { RequestMessages } from '../components/demandes/RequestMessages';
import { NotifyFollowUp } from '../components/demandes/NotifyFollowUp';
import { ServiceSatisfaction, type ServiceRating } from '../components/demandes/ServiceSatisfaction';
const answers: Record<string, string> = {
  'Où en est ma demande ?': 'Votre demande est en traitement par la Ville depuis le 25 septembre.',
  'Quand sera-t-elle traitée ?': 'Aucune date d’exécution n’est disponible pour le moment. Vous serez avisé dès que le statut change.',
  'Est-ce que je dois faire autre chose ?': 'Non, aucune action n’est requise de votre part pour l’instant.'
};
const AVERAGE_DELAY_DAYS = 14;
const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** Estime la date de traitement : date de création (jj/mm/aa) + délai moyen. */
function expectedDateAnswer(createdOn: string) {
  const [day, month, year] = createdOn.split('/').map(Number);
  const expected = new Date(2000 + year, month - 1, day + AVERAGE_DELAY_DAYS);
  return `Le délai moyen pour ce type de demande est de 10 jours ouvrables, donc la date prévue est autour du ${expected.getDate()} ${months[expected.getMonth()]}.`;
}
type Tab = 'details' | 'messages';
export function DemandeDetail() {
  const {
    id
  } = useParams();
  const {
    requests
  } = useRequests();
  const request = requests.find(item => item.id === id) ?? requests[0];
  // `?onglet=messages` ouvre directement le fil (lien « Voir les messages » d'ATNA).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(searchParams.get('onglet') === 'messages' ? 'messages' : 'details');
  const [asked, setAsked] = useState<string | null>(null);
  // Évaluation du service : la même note apparaît sous le numéro et dans le message de clôture.
  const [ratings, setRatings] = useState<Record<string, ServiceRating>>({});
  const rating = ratings[request.id] ?? null;
  const rate = (value: ServiceRating) => setRatings(current => ({
    ...current,
    [request.id]: value
  }));
  const pending = request.messages.filter(message => message.author === 'ville' && message.awaiting).length;
  const tabs: {
    id: Tab;
    label: string;
    badge?: number;
  }[] = [{
    id: 'details',
    label: 'Détails'
  }, {
    id: 'messages',
    label: 'Messages',
    badge: pending || undefined
  }];
  return <main className="bg-white" data-id="element-2984" data-mp="31dnsyzh7xslp">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-2985" data-mp="31fgpvn214ygi">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-2986" data-mp="36bfgzieqsukm">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2987" data-mp="2msi0ryqnag4y">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2988" data-mp="2wobma7mcdjyv" />
          <Link to="/mes-demandes" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2989" data-mp="2wmicwkkcgln6">
            Mes demandes
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2990" data-mp="35ohtxcvczpq4" />
          <span aria-current="page" data-id="element-2991" data-mp="31no60jhx2ax8">{request.reference}</span>
        </nav>

        <header className="mt-4" data-id="element-2992" data-mp="31nfuauxj5bnm">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-2993" data-mp="3fetbi27ytxfh">
            <div data-id="element-2994" data-mp="3fgxcocehcic2">
              <h1 className="text-4xl font-extrabold tracking-tight text-ville-navy" data-id="element-2995" data-mp="3ag9nlw9tabkh">{request.title}</h1>
              <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-2996" data-mp="3pd0lu27r1h8i">{request.category}</p>
            </div>
            <div className="flex flex-col items-end gap-2" data-id="element-2997" data-mp="3kepcdsld98qt">
              <p className="rounded-full border border-slate-200 px-4 py-2 text-sm font-bold tabular-nums text-ville-navy" data-id="element-2998" data-mp="3kccn0uda3w3q">
                <span className="sr-only" data-id="element-2999" data-mp="1mqbuq7iugxsn">Numéro de requête : </span>
                {request.reference}
              </p>
              <ServiceSatisfaction value={rating} onChange={rate} variant="compact" data-id="element-3000" data-mp="1rid1jdx8twba" />
            </div>
          </div>

          <div className="mt-5" data-id="element-3001" data-mp="1wfvd7c2napmz">
            <StatusProgress status={request.status} data-id="element-3002" data-mp="1wi955c213lqb" />
          </div>
        </header>

        <div role="tablist" aria-label="Sections de la requête" className="mt-8 flex gap-6 border-b border-slate-200" data-id="element-3003" data-mp="yuqzwb7kt1r2">
          {tabs.map(item => {
          const active = tab === item.id;
          return <button key={item.id} type="button" role="tab" id={`onglet-${item.id}`} aria-selected={active} aria-controls={`panneau-${item.id}`} onClick={() => setTab(item.id)} className={`relative -mb-px flex items-center gap-2 pb-3 text-[15px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'text-ville-navy' : 'text-slate-500 hover:text-ville-navy'}`} data-id="element-3004" data-mp="1dpx54vyukwjc">
                {item.label}
                {item.badge && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-ville-lime px-1.5 text-[11px] font-bold text-ville-navy" data-id="element-3005" data-mp="3uwdwtmkdxz4l">
                    {item.badge}
                  </span>}
                {active && <motion.span layoutId="onglet-demande" transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-ville-navy" aria-hidden="true" data-id="element-3006" data-mp="3gz8lb71wk81y" />}
              </button>;
        })}
        </div>

        {tab === 'details' ? <div role="tabpanel" id="panneau-details" aria-labelledby="onglet-details" className="mt-6" data-id="element-3007" data-mp="34mwrpwaiec9d">
            <RequestDetailsPanel request={request} data-id="element-3008" data-mp="39g951a9qmwmt" />

            <p className="mt-4 text-xs text-slate-500" data-id="element-3009" data-mp="2uqtenve4ct7n">Statut actuel : {statusLabels[request.status]}</p>

            <section aria-labelledby="atna-demande-titre" className="mt-6 rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-3010" data-mp="2zobt21awo0n1">
              <h2 id="atna-demande-titre" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-3011" data-mp="3nf8sielil5he">
                <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-3012" data-mp="3sbpp7x6s4s63" />
                Demander à ATNA
              </h2>

              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-3013" data-mp="3if355ama2554">
                {detailQuestions.map(question => <li key={question} data-id="element-3014" data-mp="220beb24s55xx">
                    <button type="button" onClick={() => setAsked(question)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${asked === question ? 'bg-ville-indigo text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-3015" data-mp="223h46jl8gbup">
                      {question}
                    </button>
                  </li>)}
              </ul>

              {asked && <p className="mt-4 rounded-2xl bg-ville-indigo-soft px-5 py-4 text-sm leading-relaxed text-ville-navy" data-id="element-3016" data-mp="f4enqg5275aa">
                  {asked === 'Quand sera-t-elle traitée ?' ? expectedDateAnswer(request.createdOn) : answers[asked]}
                </p>}

              <NotifyFollowUp live requestId={request.id} data-id="element-3017" data-mp="3irdjo3nm7sh2" />
            </section>
          </div> : <div role="tabpanel" id="panneau-messages" aria-labelledby="onglet-messages" className="mt-6" data-id="element-3018" data-mp="7hjc5fik4skj">
            <RequestMessages request={request} rating={rating} onRate={rate} data-id="element-3019" data-mp="cgmf8uez8opv" />
          </div>}
      </div>
    </main>;
}